import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, capitalizeFirstLetter, FeeLabel,Inscription,Text } from '@common-fe/common-fe';

import { PaymentMethod, PaymentMethodVerificationStatus } from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { PaymentMethodType } from '@/modules/user/user.types';
import spacings from '@/styles/spacings';
import theme from '@/styles/theme';
import { getFullAddress } from '@/utils';

interface Props {
  paymentMethod?: Partial<PaymentMethod>;
  simplifyDesc?: boolean;
  titleFontSize?: string;
  isVerifiedDateVisible?: boolean;
  showPlaidStatus?: boolean;
  paymentEmployeeFee?: string;
  hasPaymentEmployeeFee?: boolean;
  hasAccountVerificationPendingMessage?: boolean;
}

const PaymentMethodDescription: React.FC<Props> = ({
  paymentMethod,
  simplifyDesc,
  titleFontSize,
  isVerifiedDateVisible = true,
  showPlaidStatus,
  paymentEmployeeFee,
  hasPaymentEmployeeFee,
  hasAccountVerificationPendingMessage,
}) => {
  const { t } = useTranslation();
  const { type, accountNumberLast4, accountDescription, isVerified } = paymentMethod || {};
  const fee = useMemo(() => {
    if (!hasPaymentEmployeeFee) return null;
    return (
      <FeeLabel
        amount={paymentEmployeeFee}
        testId={type}
        style={{ marginTop: theme.spacings.spacing2 }}
      />
    );
  }, [paymentEmployeeFee, type, hasPaymentEmployeeFee]);
  const currentTitleFontSize = titleFontSize || spacings.m;
  const plaidAccountStatus = useMemo(() => {
    if (!paymentMethod?.verificationStatus || !showPlaidStatus) return undefined;
    if ((paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING
      || paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL)
      && !hasAccountVerificationPendingMessage) {
      return (
        <Text size="12px" color="warning" weight={500} margin={{ top: 'spacing2'}}>
          {t('Account verification pending')}
        </Text>
      );
    }
    if (paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED
      || paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED
    ) {
      return (
        <Text size="12px" color="danger" weight={500} margin={{ top: 'spacing2'}}>
          {t('Verification failed')}
        </Text>
      );
    }
  }, [paymentMethod?.verificationStatus, showPlaidStatus, t, hasAccountVerificationPendingMessage]);

  if (!paymentMethod) {
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textBody">
          {t('No payment method added')}
        </Text>
      </Box>
    );
  }

  switch (type) {
  case PaymentMethodType.PAYPAL:
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        {paymentMethod.requisites?.email && (
          <Text size={currentTitleFontSize} color="textBody" wordBreak="break-word" data-testId="payment_details_email">
            {paymentMethod.requisites?.email}
          </Text>
        )}
        {paymentMethod.requisites?.phoneNumber && (
          <Text
            size={paymentMethod.requisites?.email ? spacings.s : spacings.m}
            color={paymentMethod.requisites?.email ? 'textSecondary' : 'textBody'}
            weight={500}
            wordBreak="break-word"
            data-testId="payment_details_phone_number"
          >
            {paymentMethod.requisites?.phoneNumber}
          </Text>
        )}
        {fee}
      </Box>
    );
  case PaymentMethodType.VENMO:
    return paymentMethod.requisites?.phoneNumber && (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textBody" data-testId="payment_details_phone_number">
          {paymentMethod.requisites?.phoneNumber}
        </Text>
        {fee}
      </Box>
    );
    case PaymentMethodType.DEBIT:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={currentTitleFontSize} color="textBody" data-testId="payment_details_card">
            {capitalizeFirstLetter(paymentMethod.requisites?.cardType || '')} (ending in {paymentMethod.requisites?.cardLast4})
          </Text>
          {fee}
        </Box>
      );
    case PaymentMethodType.DIRECT_DEPOSIT:
      return (
        <Box data-testid={`PaymentMethodLabel_content_${type}`}>
          <Text size={currentTitleFontSize} color="textBody" data-testId="payment_details_account_description">
            {`${accountDescription} ${simplifyDesc ? '(ending in ****)' : `(ending in ${accountNumberLast4 || '****'})`}`}
          </Text>
        {plaidAccountStatus ? plaidAccountStatus : null}
        {hasAccountVerificationPendingMessage ? (
          <Inscription color="warning" lineHeight="20px" margin={{ top: 'spacing2'}}>
            {t('Account verification pending')}
          </Inscription>
        ) : null}
        {isVerified && isVerifiedDateVisible ? (
          <Box direction="row" gap={spacings.xxxs}>
            <Text size={spacings.s} color="textSecondary" weight={500}>
              Verified on
            </Text>
            <Text size={spacings.s} color="success" weight={500}>
              06/28/21
            </Text>
          </Box>
        ) : null}
        {fee}
      </Box>
    );
  case PaymentMethodType.CHECK:
    return (
      <Box data-testid={`PaymentMethodLabel_content_${type}`}>
        <Text size={currentTitleFontSize} color="textBody" data-testId="payment_details_address">
          {getFullAddress(paymentMethod?.address)}
        </Text>
        {fee}
      </Box>
    );

  default:
    return null;
  }
};

export default PaymentMethodDescription;
